import { Injectable, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, of, forkJoin, Subscription } from 'rxjs';
import { mergeMap, map } from 'rxjs/operators';
import { PhotoService } from '../_services/photo.service';
import { PaymentService } from '../_services/payment-service.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { DialogService } from '../_services/dialog.service';
import { OnDeactivate } from '../_services/can-deactivate-guard.service';
import { UserService } from '../_services/user.service';
import { FormHelpers } from '../_shared/form-helpers';
import { DBConfig, NgxIndexedDBService } from 'ngx-indexed-db';

import { CUEServiceBase, LiveServiceFileCollection } from '../_services/cue-base.service';
import { Environment } from '../../environments/environment';

/**
 * Base class for CUEService create/edit components (ls/sc/tg)
 * Handles payments (actual process, spinner, couponcode) and navigation
 * */

@Injectable()
export abstract class BaseCueServiceComponent<TFormData, TListData> implements OnDeactivate, OnInit, OnDestroy {
  public id: number;
  public serviceId: number;
  public form: FormGroup;
  public checkoutAgainOnError = false;

  // todo: move coupon verification here (from the coupon component)
  public couponCode = '';

  public loading = false;
  public loadingMessage = '';
  public hasValidationErrors = false;

  public notifyCreator: boolean = true;

  public currentLocation: string = '';

  public buildMessages = [
    'Ready to go!',
    'All set!',
    'You\'re on your way!',
    'Time to shine!',
    'Hooray!'
  ];

  public storageKey: string;

  public saveSubscription: Subscription;

  public abstract serviceFiles: LiveServiceFileCollection;

  constructor(
    protected cueService: CUEServiceBase<TFormData, TListData>,
    protected photoService: PhotoService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected spinner: NgxSpinnerService,
    protected paymentService: PaymentService,
    protected dialogService: DialogService,
    protected viewRef: ViewContainerRef,
    protected userService: UserService,
    protected dbService: NgxIndexedDBService
  ) {}

  /** Gets the initial data when creating a new service */
  protected abstract getInitialDataForNewItem(): Observable<TFormData>;
  /** Put data into the main FormGroup */
  protected abstract setFormData(data: TFormData, userSubscription: any, savedData?: TFormData): FormGroup;
  /** Reads data from the main FormGroup */
  protected abstract getFormData(form: FormGroup, includeServiceFiles: boolean): TFormData;

  protected abstract loadServiceFiles(data: TFormData);

  // optional overrides
  /** If the component loads any secondary data, it should be done here and map or tap should be used to capture the results into a member variable. */
  protected loadLookupData(): Observable<any> {
    return of(null);
  }
  /** Override this to highlight controls with errors. This method gets called if the form is invalid when checkout is called. */
  protected onCheckoutValidationError() {}
  protected uploadContent(): Observable<any> {
    return of(null);
  }
  /** Override to provide a custom succes message and/or do any post success processing. */
  protected onBuildSuccess(): string {
    return `Your ${this.cueService.getServiceDisplayName()} has been ${this.id ? 'updated' : 'purchased'} successfully!`;
  }
  /** Overrid to provide a custom error message and/or do any post error processing. */
  protected onBuildError(error: any): { checkoutAgain: boolean; title: string; message: string } {
    // Back-end passes back a 402 when payment is declined
    if (error.status === 402) {
      return {
        checkoutAgain: this.checkoutAgainOnError,
        title: 'Card Declined',
        message: 'The card number you attempted to use on this purchase was declined by Stripe, we apologize for the inconvenience'
      };
    }

    if (!this.form.valid) {
      this.onCheckoutValidationError();
      return {
        checkoutAgain: false,
        title: 'Error uploading files',
        message: 'We were unable to process some of the files you uploaded. Please check the form for specific validation messages.'
      };
    } else {
      return {
        checkoutAgain: this.checkoutAgainOnError,
        title: 'Oops something went wrong...',
        message: 'We were unable to process your request at this time, please try again in a few moments. Please contact CUE support at support@connectwithcue.com if you continue to experience issues'
      };
    }
  }

  ngOnInit() {

    this.cueService.startSaveInterval();

    this.setWorkingStatus('Loading...');
    this.photoService.clearQueue();
    this.currentLocation = window.location.href;
    this.route.params.pipe(
      mergeMap(params => {
        this.id = params['id'];
        this.serviceId = params['serviceId'];
        return forkJoin([
          this.loadLookupData(),
          this.id ? this.cueService.getById(this.id) : this.getInitialDataForNewItem(),
          this.userService.checkUserSubscription()
        ]);
      }))
      .subscribe(
        data => {
          const userSubscription = data[2];
          if (!userSubscription.valid) {
            this.dialogService
              .alert(
                'Subscription Expired',
                'Your subscription to CUE services has expired. To renew your subscription, please contact subscribe@cueaudio.com',
                this.viewRef
              )
              .then(() => {
                this.form.markAsPristine();
                this.navigateUp();
              });
          }

          // return to the landing page if no results are found. could be a bad id, or someone might not have access
          if (!data[1]) {
            this.router.navigate(['/portal/' + localStorage.getItem('HomeRoute')]);
          }

          this.saveSubscription = this.cueService.saveToLocalStorage
            .subscribe(
              _ => {

                this.dbService.update('cue-live-services', { id: this.storageKey, formData: this.getFormData(this.form, true) }).subscribe();
              },
              error => {

                console.log(error);
              }
            );

          this.storageKey = this.id ? this.cueService.getServiceDisplayName() + `-${this.id}` : "new-" + this.cueService.getServiceDisplayName();

          this.dbService.getByID('cue-live-services', this.storageKey)
            .subscribe(
              async (formData: { id: string, formData: TFormData } )=> {

                if (formData) {

                  const result = await this.dialogService.restorePrompt(
                    `Restore Saved ${this.cueService.getServiceDisplayName()}?`,
                    `We\'ve recovered your most recent saved ${this.cueService.getServiceDisplayName()} from local files on your computer, 
                    would you like to pick up where you left off?`,
                    this.viewRef
                  );
      
                  if (result) {
                    
                    this.form = this.setFormData(formData.formData as TFormData, userSubscription, data[1]);
                    this.loadServiceFiles(formData.formData as TFormData);

                  } else {

                    this.dbService.deleteByKey('cue-live-services', this.storageKey)
                      .subscribe(
                        _ => {

                          this.form = this.setFormData(data[1], userSubscription);
                        },
                        error => {

                          console.log(error);
                        }
                      )

                  }
      
                } else {
                  
                  this.form = this.setFormData(data[1], userSubscription);
                }
              },
              error => {

                console.log(error);
                this.saveSubscription.unsubscribe();
                this.cueService.cleanUp();
                this.form = this.setFormData(data[1], userSubscription);
              }
            );

          this.loading = false;
          this.spinner.hide();
        },
        err => {
          console.error(err);
          this.loading = false;
          this.spinner.hide();
        }
      );
  }

  public onNavigatingAway() {
    if (!this.form || !this.form.dirty) {
      return true;
    } else {
      return this.dialogService.confirm('Unsaved Changes', 'Are you sure you want to continue? Any unsaved changes will be lost!', this.viewRef);
    }
  }

  private setWorkingStatus(text: string = null) {
    if (text) {
      this.loadingMessage = text;

      if (!this.loading) {
        this.loading = true;
        this.spinner.show();
      }
    } else {
      this.loading = false;
      this.spinner.hide();
    }
  }

  public checkout() {
    // force validation
    this.hasValidationErrors = !this.form.valid;
    if (this.hasValidationErrors) {
      FormHelpers.processControlRecursively(this.form, c => c.markAsDirty());
      FormHelpers.printControlState(this.form, c => !c.valid);
      this.onCheckoutValidationError();
      return;
    }

    this.setWorkingStatus('Calculating payment... Please do not close this window or hit the back button');
    
    this.cueService
      .calculatePrice(this.id, this.getFormData(this.form, false), this.couponCode)
      .pipe(
        // 1. authorize payment
        mergeMap(pi => {
          // don't want to show spinner behind stripe popup because we can't hide it if user cancels payment (stripe does not fire event for it)
          this.checkoutAgainOnError = pi.totalAmount > 0;
          this.setWorkingStatus('Authorizing payment... Please do not close this window or hit the back button');
          return this.paymentService.authorizePayment(pi);
        }),
        // 2. perform uploads but return payment authorization for confirming payment in the next step
        mergeMap(pa => {
          this.setWorkingStatus('Processing and uploading your files... Please do not close this window or hit the back button');
          return forkJoin([this.uploadContent(), this.photoService.commitUploads()]).pipe(map(() => pa));
        }),
        // 3. save data
        mergeMap(pa => {
          this.setWorkingStatus(`Processing ${this.cueService.getServiceDisplayName()}... Please do not close this window or hit the back button`);
          return this.cueService.save(this.id, this.getFormData(this.form, false), this.couponCode, pa.token, pa.amount, this.notifyCreator);
        })
      )
      .subscribe(
        (res) => {

          this.dbService.deleteByKey('cue-live-services', this.storageKey).subscribe();

          this.setWorkingStatus(null);
          const message = this.onBuildSuccess();
          this.dialogService.confirm(this.buildMessages[Math.floor(Math.random() * 5)], message + ' Would you like to continue editing?', this.viewRef)
            .then((result) =>{
              if (!result){

                this.form.markAsPristine();
                this.navigateUp();
              } else {
                if (!this.id){
                  const lastForwardSlashIdx = this.router.url.lastIndexOf('/');
                  const path = this.router.url.slice(0, lastForwardSlashIdx);
                  this.form.markAsPristine();
                  this.router.navigate([path + `/${res.id[1]}` + `/${res.id[0]}/`]);
                }
              }
            });
        },
        error => {
          this.setWorkingStatus(null);
          !Environment.production ? console.log(error) : console.log('There was error processing this request');
          const res = this.onBuildError(error);
          this.dialogService.error(error, res.message, this.viewRef).then(() => {
            if (res.checkoutAgain) {
              this.checkout();
            }
          });
        }
      );
  }

  public navigateUp() {
    this.router.navigate([this.id > 0 ? '../../' : '../'], {relativeTo: this.route});
  }

  public setSpinnerState(state: boolean) {

    if (state) {

      this.spinner.show();

    } else {

      this.spinner.hide();
    }
  }

  public setLoadingMessage(message: string) {

    this.loadingMessage = message;
  }

  protected isEmailValid(email) {
    return /(?:[A-z0-9!#$%&amp;&quot;*+/=?^_`{|}~-]+(?:\.[A-z0-9!#$%&amp;&quot;*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[A-z0-9](?:[A-z0-9-]*[A-z0-9])?\.)+[A-z0-9](?:[A-z0-9-]*[A-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[A-z0-9-]*[A-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/.test(email);
  }

  public ngOnDestroy(): void {
    
    this.saveSubscription.unsubscribe();
    this.cueService.cleanUp();
  }
}

export const cueServicesDbConfig: DBConfig = {
  name: "CueClientDB",
  version: 1,
  objectStoresMeta: [{
    store: "cue-live-services",
    storeConfig: { keyPath: 'id', autoIncrement: false },
    storeSchema: [
      { name: 'id', keypath: 'id', options: { unique: true }},
      { name: 'formData', keypath: 'formData', options: { unique: false }}
    ]
  }]
}
